/*
  Namea – personalisering på produktsiden.
  - Viser/skjuler felterne efter den valgte variant (fx "Uden navn", "Med navn", "Med navn og figur").
  - Skjulte felter deaktiveres, så de ikke kommer med på ordren.
  - Tjekker navn, garnfarve, figur og stavebekræftelse, før varen lægges i kurven.
  - Bygger en skjult engelsk linje til fabrikken:
    "Item: Corduroy backpack | Color: Dusty pink | Name: Søren | Yarn: Grass | Icon: A17".
    Ordene foran koderne kommer fra opsætningen (factoryKeys), fx "Thread" og "Font" på træhus-rygsækken.
  - Kan bruges flere gange på samme side (pakker): alle blokke tjekkes, og den første fejl får fokus.

  Kort-design:
  - Farveknapperne i kortet (data-namea-option) styrer den skjulte variantvælger, så pris, lager og
    billeder følger med som før.
  - "Se garnet i virkeligheden" og lignende knapper (data-namea-toggle) folder et billede ud.
  - "Se blomsterne på billedet" (data-namea-jump) viser oversigtsbilledet i galleriet.
  - Garnfarve og figur er dropdown-knapper (data-namea-picker), der lukker igen, når der er valgt.
  - Over "Læg i kurv" vises en opsummering af alle kortenes valg ("Jeres valg" / "Jeres sæt").
  - Når kunden går i gang i et kort på en side med flere kort, skifter hovedbilledet til den taske.
*/
(function () {
  if (customElements.get('namea-personalization')) return;

  function currentVariantSelects() {
    const all = document.querySelectorAll('variant-selects');
    for (let i = 0; i < all.length; i++) {
      if (all[i].style.display !== 'none') return all[i];
    }
    return all[0] || null;
  }

  function optionWrapper(optionName) {
    const selects = currentVariantSelects();
    if (!selects) return null;
    return (
      Array.from(selects.querySelectorAll('[data-option-name]')).find(
        (el) => el.getAttribute('data-option-name') === optionName
      ) || null
    );
  }

  function selectedValueOf(wrapper) {
    if (!wrapper) return null;
    const checked = wrapper.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    const select = wrapper.querySelector('select');
    return select ? select.value : null;
  }

  // Farver på engelsk til fabrikken. Ukendte farver sendes, som de står.
  const FACTORY_COLORS = {
    'beige': 'Beige', 'støvet rosa': 'Dusty pink', 'lyserød': 'Light pink', 'khaki': 'Khaki',
    'blå': 'Blue', 'rød': 'Red', 'brun': 'Brown', 'gul': 'Yellow', 'grøn': 'Green', 'sort': 'Black',
    'hvid': 'White', 'creme': 'Cream', 'grå': 'Grey', 'lilla': 'Purple', 'rosa': 'Pink', 'mint': 'Mint',
    'kobber brun': 'Copper brown', 'mørk gul': 'Dark yellow', 'lime grøn': 'Lime green', 'mint grøn': 'Mint green',
    'jordbær creme': 'Strawberry on cream', 'jordbær rosa': 'Strawberry on pink', 'ternet beige': 'Beige check',
  };
  function factoryColor(value) {
    const key = String(value || '').trim().toLowerCase();
    return FACTORY_COLORS[key] || String(value || '').trim();
  }

  /* ---------- Opsummering over "Læg i kurv" ---------- */

  const Summary = {
    scheduled: false,

    render() {
      if (this.scheduled) return;
      this.scheduled = true;
      window.requestAnimationFrame(() => {
        this.scheduled = false;
        this.draw();
      });
    },

    box() {
      let box = document.querySelector('[data-namea-summary]');
      if (box) return box;
      const form = document.querySelector('product-info product-form') || document.querySelector('product-form');
      if (!form) return null;
      const anchor = form.parentElement && form.parentElement !== document.body ? form.parentElement : form;
      box = document.createElement('div');
      box.className = 'namea-summary';
      box.setAttribute('data-namea-summary', '');
      box.setAttribute('aria-live', 'polite');
      anchor.parentNode.insertBefore(box, anchor);
      return box;
    },

    draw() {
      const cards = Array.from(document.querySelectorAll('namea-personalization')).filter(
        (card) => card.config && !card.hidden
      );
      const box = this.box();
      if (!box) return;
      if (!cards.length) {
        box.hidden = true;
        return;
      }
      box.hidden = false;
      box.textContent = '';

      const title = document.createElement('p');
      title.className = 'namea-summary__title';
      title.textContent = cards.length > 1 ? 'Jeres sæt' : 'Jeres valg';
      box.appendChild(title);

      // Valgmuligheder, der ikke hører til et bestemt kort (fx "Mønster" på et sæt).
      const assigned = new Set();
      cards.forEach((card) => card.cardOptions.forEach((name) => assigned.add(name)));
      const shared = [];
      const selects = currentVariantSelects();
      if (selects) {
        selects.querySelectorAll('[data-option-name]').forEach((wrapper) => {
          const name = wrapper.getAttribute('data-option-name');
          if (assigned.has(name)) return;
          const value = selectedValueOf(wrapper);
          if (value) shared.push(`${name}: ${value}`);
        });
      }
      if (shared.length) {
        const row = document.createElement('p');
        row.className = 'namea-summary__text';
        row.textContent = shared.join(' · ');
        box.appendChild(row);
      }

      cards.forEach((card, index) => {
        const row = document.createElement('div');
        row.className = 'namea-summary__row';
        if (cards.length > 1) {
          const num = document.createElement('span');
          num.className = 'namea-summary__num';
          num.setAttribute('aria-hidden', 'true');
          num.textContent = String(index + 1);
          row.appendChild(num);
        }
        const body = document.createElement('div');
        body.className = 'namea-summary__body';
        if (cards.length > 1 && card.dataset.cardTitle) {
          const name = document.createElement('span');
          name.className = 'namea-summary__name';
          name.textContent = card.dataset.cardTitle;
          body.appendChild(name);
        }
        const text = document.createElement('span');
        text.className = 'namea-summary__text';
        card.summaryParts().forEach((part, i) => {
          if (i > 0) text.appendChild(document.createTextNode(' · '));
          if (part.missing) {
            const missing = document.createElement('span');
            missing.className = 'namea-summary__missing';
            missing.textContent = part.text;
            text.appendChild(missing);
          } else {
            text.appendChild(document.createTextNode(part.text));
          }
        });
        body.appendChild(text);
        row.appendChild(body);
        box.appendChild(row);
      });
    },
  };

  customElements.define(
    'namea-personalization',
    class NameaPersonalization extends HTMLElement {
      connectedCallback() {
        const configEl = this.querySelector('[data-namea-config]');
        if (!configEl) return;
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.error('Namea: kunne ikke læse personaliseringens opsætning', error);
          return;
        }

        this.groups = {};
        this.querySelectorAll('[data-group]').forEach((group) => {
          this.groups[group.dataset.group] = group;
        });
        this.nameInput = this.querySelector('[data-name-input]');
        this.nameCount = this.querySelector('[data-name-count]');
        this.confirmInput = this.querySelector('[data-confirm]');
        this.summaryInput = this.querySelector('[data-summary]');
        this.cardOptions = String(this.dataset.cardOptions || '')
          .split('§')
          .filter(Boolean);

        this.hideValues = this.toList(this.config.hideValues);
        this.figureValues = this.toList(this.config.figureValues);

        this.onInput = this.onInput.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDocumentChange = this.onDocumentChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);

        this.addEventListener('input', this.onInput);
        this.addEventListener('change', this.onInput);
        this.addEventListener('click', this.onClick);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('pointerdown', () => {
          this.lastPointer = Date.now();
        });
        document.addEventListener('submit', this.onSubmit, true);
        document.addEventListener('change', this.onDocumentChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event && event.data && event.data.variant;
            if (variant && Array.isArray(variant.options)) this.applyOptions(variant.options);
            this.syncOptionButtons();
            this.updateSummary();
            Summary.render();
          });
        }

        this.applyOptions(this.config.initialOptions || []);
        this.updateCount();
        this.updateSelectedLabels();
        this.updateSummary();
        this.syncOptionButtons();
        Summary.render();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        document.removeEventListener('change', this.onDocumentChange);
        if (this.unsubscribe) this.unsubscribe();
      }

      toList(value) {
        return String(value || '')
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean);
      }

      /* ---------- Farveknapper i kortet ---------- */

      onClick(event) {
        const toggle = event.target.closest('[data-namea-toggle]');
        if (toggle && this.contains(toggle)) {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          if (panel) panel.hidden = !open;
          const label = open ? toggle.dataset.openLabel : toggle.dataset.closedLabel;
          if (label) toggle.textContent = label;
          return;
        }
        const picker = event.target.closest('[data-namea-picker]');
        if (picker && this.contains(picker)) {
          this.setPicker(picker, picker.getAttribute('aria-expanded') !== 'true');
          return;
        }
        const jump = event.target.closest('[data-namea-jump]');
        if (jump && this.contains(jump)) {
          const gallery = document.querySelector('media-gallery');
          if (gallery && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(jump.dataset.nameaJump, false);
          }
          return;
        }
        const button = event.target.closest('[data-namea-option]');
        if (button && this.contains(button)) this.chooseOption(button);
      }

      /* ---------- Dropdown til garnfarve og figur ---------- */

      setPicker(picker, open) {
        const panel = document.getElementById(picker.getAttribute('aria-controls'));
        picker.setAttribute('aria-expanded', String(open));
        if (panel) panel.hidden = !open;
      }

      updatePickers() {
        this.querySelectorAll('[data-namea-picker]').forEach((picker) => {
          const panel = document.getElementById(picker.getAttribute('aria-controls'));
          const checked = panel && panel.querySelector('input[type="radio"]:checked');
          const text = picker.querySelector('[data-picker-text]');
          const ball = picker.querySelector('[data-picker-ball]');
          if (text) text.textContent = checked ? checked.dataset.label : picker.dataset.placeholder || '';
          picker.classList.toggle('is-chosen', Boolean(checked));
          if (ball) {
            const source = checked && checked.parentElement.querySelector('.namea-p__ball');
            const style = source ? source.getAttribute('style') : '';
            ball.hidden = !style;
            if (style) ball.setAttribute('style', style);
          }
        });
      }

      chooseOption(button) {
        const optionName = button.dataset.nameaOption;
        const value = button.dataset.value;
        const wrapper = optionWrapper(optionName);
        if (!wrapper) return;

        let done = false;
        wrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
          if (done || radio.value !== value) return;
          done = true;
          if (!radio.checked) radio.click();
        });
        if (!done) {
          const select = wrapper.querySelector('select');
          if (select && select.value !== value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        this.markOption(optionName, value);
        this.updateSummary();
        Summary.render();
      }

      markOption(optionName, value) {
        let selectedButton = null;
        this.querySelectorAll('[data-namea-option]').forEach((button) => {
          if (button.dataset.nameaOption !== optionName) return;
          const isSelected = button.dataset.value === value;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
          if (isSelected) selectedButton = button;
        });
        this.querySelectorAll('[data-namea-opt-selected]').forEach((label) => {
          if (label.getAttribute('data-namea-opt-selected') === optionName && value) label.textContent = value;
        });
        // Kortets lille billede følger kortets første valgmulighed.
        if (selectedButton && this.cardOptions[0] === optionName && selectedButton.dataset.img) {
          const thumb = this.querySelector('[data-namea-card-thumb] img');
          if (thumb) {
            thumb.removeAttribute('srcset');
            thumb.src = selectedButton.dataset.img;
          }
        }
      }

      syncOptionButtons() {
        const names = new Set();
        this.querySelectorAll('[data-namea-option]').forEach((button) => names.add(button.dataset.nameaOption));
        names.forEach((optionName) => {
          const wrapper = optionWrapper(optionName);
          if (!wrapper) return;
          this.markOption(optionName, selectedValueOf(wrapper));
          this.querySelectorAll('[data-namea-option]').forEach((button) => {
            if (button.dataset.nameaOption !== optionName) return;
            const radio = Array.from(wrapper.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === button.dataset.value
            );
            if (radio) button.classList.toggle('is-unavailable', radio.classList.contains('disabled'));
          });
        });
      }

      /* ---------- Hovedbilledet følger det kort, kunden er i gang med ---------- */

      onFocusIn(event) {
        if (document.querySelectorAll('namea-personalization').length < 2) return;
        if (event.target.closest('[data-namea-option]')) return;
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.showOwnMedia();
      }

      showOwnMedia() {
        if (typeof window.nameaShowMedia !== 'function') return;
        const selected = this.querySelector('.namea-p__opt.is-selected');
        if (selected) {
          window.nameaShowMedia(selected.dataset.nameaOption, selected.dataset.value);
          return;
        }
        // Kort uden egne farveknapper: prøv "Kortets titel – valgt værdi", fx "Madpakketaske – Jordbær rosa".
        const title = this.dataset.cardTitle;
        const selects = currentVariantSelects();
        if (!title || !selects) return;
        const wrappers = selects.querySelectorAll('[data-option-name]');
        for (let i = 0; i < wrappers.length; i++) {
          const value = selectedValueOf(wrappers[i]);
          if (value && window.nameaShowMedia(title, value)) return;
        }
      }

      /* ---------- Variant: hvilke felter skal vises ---------- */

      applyOptions(options) {
        const index = this.config.triggerIndex;
        let showName = true;
        let showFigure = Boolean(this.groups.figure);

        if (index >= 0) {
          const value = String(options[index] || '').trim().toLowerCase();
          showName = !this.hideValues.includes(value);
          showFigure = showName && this.figureValues.includes(value);
        }

        this.state = { showName, showFigure };
        this.toggleGroup('name', showName);
        this.toggleGroup('yarn', showName);
        this.toggleGroup('confirm', showName);
        this.toggleGroup('figure', showFigure);
        // Kortet skjules kun helt, hvis det ikke også har farveknapper.
        this.hidden = !showName && !showFigure && !this.querySelector('[data-namea-option]');
        this.updateSummary();
        Summary.render();
      }

      toggleGroup(key, visible) {
        const group = this.groups[key];
        if (!group) return;
        group.hidden = !visible;
        group.querySelectorAll('input').forEach((input) => {
          input.disabled = !visible;
        });
        if (!visible) this.clearError(group);
      }

      onDocumentChange(event) {
        // Reserve, hvis Dawns pubsub ikke er tilgængelig: læs valgte værdier direkte fra variantvælgeren.
        if (typeof subscribe === 'function') return;
        const selects = event.target.closest('variant-selects');
        if (!selects) return;
        window.setTimeout(() => {
          const current = currentVariantSelects() || selects;
          const values = [];
          current.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((wrapper) => {
            const checked = wrapper.querySelector('input[type="radio"]:checked');
            const select = wrapper.querySelector('select');
            if (checked) values.push(checked.value);
            else if (select) values.push(select.options[select.selectedIndex]?.textContent.trim() || select.value);
          });
          if (values.length) this.applyOptions(values);
          this.syncOptionButtons();
        }, 0);
      }

      /* ---------- Input ---------- */

      onInput(event) {
        if (event.target === this.nameInput) {
          this.updateCount();
          if (event.type === 'change') this.nameInput.value = this.cleanName(this.nameInput.value);
          this.clearError(this.groups.name);
        } else if (event.target.type === 'radio') {
          this.clearError(event.target.closest('[data-group]'));
          this.updateSelectedLabels();
          // Valgt med mus/finger: luk dropdownen igen. Med tastaturet bliver den åben.
          const panel = event.target.closest('.namea-p__picker-panel');
          if (event.type === 'change' && panel && Date.now() - (this.lastPointer || 0) < 1500) {
            const picker = this.querySelector(`[aria-controls="${panel.id}"]`);
            if (picker) {
              this.setPicker(picker, false);
              picker.focus({ preventScroll: true });
            }
          }
        } else if (event.target === this.confirmInput) {
          this.clearError(this.groups.confirm);
        }
        this.updateSummary();
        Summary.render();
      }

      cleanName(value) {
        return String(value || '').replace(/\s+/g, ' ').trim();
      }

      updateCount() {
        if (!this.nameInput || !this.nameCount) return;
        this.nameCount.textContent = `${this.nameInput.value.length}/${this.config.maxLength}`;
      }

      updateSelectedLabels() {
        ['yarn', 'figure'].forEach((key) => {
          const group = this.groups[key];
          if (!group) return;
          const label = group.querySelector('[data-selected-label]');
          const checked = group.querySelector('input[type="radio"]:checked');
          if (label) label.textContent = checked ? `: ${checked.dataset.label}` : '';
        });
        this.updatePickers();
      }

      selected(key) {
        const group = this.groups[key];
        if (!group || group.hidden) return null;
        return group.querySelector('input[type="radio"]:checked');
      }

      // Kortets linje i opsummeringen: [{ text, missing }]
      summaryParts() {
        const parts = [];
        this.querySelectorAll('.namea-p__opt.is-selected').forEach((button) => {
          parts.push({ text: button.dataset.value });
        });
        const showName = this.state && this.state.showName;
        if (showName && this.nameInput && this.groups.name && !this.groups.name.hidden) {
          const name = this.cleanName(this.nameInput.value);
          parts.push(name ? { text: `»${name}«` } : { text: 'mangler navn', missing: true });
        }
        if (this.groups.yarn && !this.groups.yarn.hidden) {
          const yarn = this.selected('yarn');
          const word = String(this.config.factoryKeys && this.config.factoryKeys.yarn) === 'Thread' ? 'tråd' : 'garn';
          parts.push(yarn ? { text: `${yarn.dataset.label} ${word}` } : { text: `mangler ${word}farve`, missing: true });
        }
        if (this.groups.figure && !this.groups.figure.hidden) {
          const figure = this.selected('figure');
          parts.push(figure ? { text: figure.dataset.label } : { text: 'mangler pynt', missing: true });
        }
        return parts;
      }

      updateSummary() {
        if (!this.summaryInput || !this.config.factorySummary) return;
        const keys = this.config.factoryKeys || {};
        const parts = [];
        if (this.config.factoryItem) parts.push(`Item: ${this.config.factoryItem}`);
        // Farven: kortets egen farveknap, ellers produktets første valgmulighed (fx mønsteret på et sæt).
        const ownColor = this.querySelector('.namea-p__opt.is-selected');
        let color = ownColor ? ownColor.dataset.value : null;
        if (!color) {
          const selects = currentVariantSelects();
          color = selectedValueOf(selects && selects.querySelector('[data-option-name]'));
        }
        if (color) parts.push(`Color: ${factoryColor(color)}`);
        const showName = this.state && this.state.showName;
        if (showName && this.nameInput && this.cleanName(this.nameInput.value)) {
          parts.push(`Name: ${this.cleanName(this.nameInput.value)}`);
        }
        const yarn = this.selected('yarn');
        if (yarn) parts.push(`${keys.yarn || 'Yarn'}: ${yarn.dataset.code}`);
        const figure = this.selected('figure');
        if (figure) parts.push(`${keys.figure || 'Icon'}: ${figure.dataset.code}`);

        this.summaryInput.value = parts.join(' | ');
        this.summaryInput.disabled = parts.length === 0;
        this.summaryInput.dataset.updated = String(Date.now());
      }

      /* ---------- Validering ---------- */

      onSubmit(event) {
        const form = event.target;
        // Brug getAttribute: formularen har et felt med name="id", som skygger for form.id
        if (!form || typeof form.getAttribute !== 'function' || form.getAttribute('id') !== this.config.formId) return;
        // Har en anden blok (fx den første taske i en pakke) allerede stoppet købet, får den fokus.
        const alreadyBlocked = event.defaultPrevented;
        if (this.validate(!alreadyBlocked)) {
          this.updateSummary();
          return;
        }
        // stopPropagation (ikke stopImmediatePropagation): købet stoppes, men de andre blokke
        // på siden tjekker stadig deres felter og viser deres fejl.
        event.preventDefault();
        event.stopPropagation();
      }

      validate(focus = true) {
        const messages = this.config.messages || {};
        let firstInvalid = null;
        const fail = (key, message, focusEl) => {
          this.showError(this.groups[key], message);
          if (!firstInvalid) firstInvalid = focusEl;
        };

        if (this.state && this.state.showName) {
          if (this.nameInput) {
            const name = this.cleanName(this.nameInput.value);
            this.nameInput.value = name;
            this.updateCount();
            const pattern = this.config.allowSpecial
              ? /^[A-Za-zÆØÅæøåÄÖÜäöüÉéÈèÁáÍíÓóÚúÑñ' -]+$/
              : /^[A-Za-z' -]+$/;
            if (!name) fail('name', messages.nameMissing, this.nameInput);
            else if (name.length > this.config.maxLength) fail('name', messages.nameTooLong, this.nameInput);
            else if (!pattern.test(name)) fail('name', messages.nameInvalid, this.nameInput);
          }
          if (this.groups.yarn && !this.selected('yarn')) {
            fail('yarn', messages.yarnMissing, this.groups.yarn.querySelector('input[type="radio"]'));
          }
          if (this.confirmInput && !this.confirmInput.checked) {
            fail('confirm', messages.confirmMissing, this.confirmInput);
          }
        }
        if (this.state && this.state.showFigure && this.groups.figure && !this.selected('figure')) {
          fail('figure', messages.figureMissing, this.groups.figure.querySelector('input[type="radio"]'));
        }

        if (firstInvalid) {
          if (focus) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.focus({ preventScroll: true });
          }
          return false;
        }
        return true;
      }

      showError(group, message) {
        if (!group) return;
        const error = group.querySelector('[data-error]');
        if (error) {
          error.textContent = message || '';
          error.hidden = false;
          if (!error.id) error.id = `${this.id}-${group.dataset.group}-error`;
        }
        group.classList.add('namea-p__group--error');
        const picker = group.querySelector('[data-namea-picker]');
        if (picker) this.setPicker(picker, true);
        group.querySelectorAll('input:not([type="hidden"])').forEach((input) => {
          input.setAttribute('aria-invalid', 'true');
          if (error && input.type !== 'text') input.setAttribute('aria-describedby', error.id);
        });
      }

      clearError(group) {
        if (!group) return;
        const error = group.querySelector('[data-error]');
        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
        group.classList.remove('namea-p__group--error');
        group.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
      }
    }
  );
})();
